import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, ROLE_HOME } from "@/context/AuthContext";
import { useCountry } from "@/context/CountryContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Bell, User, LogOut, LayoutDashboard, Tag, Wrench, Search, Menu, UtensilsCrossed, BedDouble, Car, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import PriceTicker from "@/components/PriceTicker";

const PAYS = [
  { code: "CI", label: "Côte d'Ivoire", flag: "🇨🇮", currency: "FCFA" },
  { code: "GN", label: "Guinée",         flag: "🇬🇳", currency: "FG"   },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { country, setCountry, config } = useCountry();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);

  const loadNotifs = async () => {
    if (!user) return;
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch {}
  };
  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 20000); return () => clearInterval(t); }, [user]); // eslint-disable-line
  const unread = notifs.filter((n) => !n.read).length;

  const markRead = async () => { try { await api.post("/notifications/read"); loadNotifs(); } catch {} };

  return (
    <>
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-white/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-display font-extrabold">L</div>
          <span className="font-display font-bold text-lg text-secondary hidden sm:block">Les<span className="text-primary">Meilleurs</span>Prix</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          <Link to="/recherche" data-testid="nav-search"><Button variant="ghost" size="sm"><Search className="w-4 h-4 mr-1" />Comparer</Button></Link>
          <Link to="/promotions" data-testid="nav-promos"><Button variant="ghost" size="sm"><Tag className="w-4 h-4 mr-1" />Promos</Button></Link>
          <Link to="/prestataires" data-testid="nav-providers"><Button variant="ghost" size="sm"><Wrench className="w-4 h-4 mr-1" />Prestataires</Button></Link>
          <Link to="/restaurants"><Button variant="ghost" size="sm"><UtensilsCrossed className="w-4 h-4 mr-1" />Restaurants</Button></Link>
          <Link to="/hebergements"><Button variant="ghost" size="sm"><BedDouble className="w-4 h-4 mr-1" />Hébergements</Button></Link>
          <Link to="/transport"><Button variant="ghost" size="sm"><Car className="w-4 h-4 mr-1" />Transport</Button></Link>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {/* Sélecteur pays */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1 text-sm font-medium px-2">
                <span>{config.flag}</span>
                <span className="hidden sm:inline text-xs text-muted-foreground">{config.currency}</span>
                <ChevronDown className="w-3 h-3 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Changer de pays</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {PAYS.map((p) => (
                <DropdownMenuItem key={p.code} onClick={() => setCountry(p.code)}
                  className={`gap-2 ${country === p.code ? "bg-primary/5 font-semibold" : ""}`}>
                  <span>{p.flag}</span>
                  <span className="flex-1">{p.label}</span>
                  <span className="text-xs text-muted-foreground">{p.currency}</span>
                  {country === p.code && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {user ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="notif-bell" className="relative p-2 rounded-full hover:bg-muted transition-colors" onClick={markRead}>
                    <Bell className="w-5 h-5 text-secondary" />
                    {unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 text-[10px] bg-primary text-white rounded-full flex items-center justify-center">{unread}</span>}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notifs.length === 0 && <div className="px-3 py-4 text-sm text-muted-foreground">Aucune notification</div>}
                  {notifs.slice(0, 8).map((n) => (
                    <div key={n.notif_id} className="px-3 py-2 text-sm border-b border-border/50 last:border-0">
                      <div className="font-medium text-secondary">{n.title}</div>
                      <div className="text-muted-foreground text-xs">{n.body}</div>
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="account-menu" className="gap-2">
                    <User className="w-4 h-4" /><span className="hidden sm:inline">{user.name?.split(" ")[0]}</span>
                    <Badge variant="secondary" className="capitalize hidden sm:inline">{user.role}</Badge>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(ROLE_HOME[user.role])} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" />Mon espace
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/messages")}>Messages</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="login-link"><Button variant="ghost" size="sm">Connexion</Button></Link>
              <Link to="/register" data-testid="register-link"><Button size="sm" className="bg-primary hover:bg-primary/90">S'inscrire</Button></Link>
            </>
          )}
          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-border bg-white px-4 py-2 flex flex-col gap-1" data-testid="mobile-menu">
          <Link to="/recherche" onClick={() => setOpen(false)} className="py-2">Comparer les prix</Link>
          <Link to="/promotions" onClick={() => setOpen(false)} className="py-2">Promotions</Link>
          <Link to="/prestataires" onClick={() => setOpen(false)} className="py-2">Prestataires</Link>
          <Link to="/restaurants" onClick={() => setOpen(false)} className="py-2">Restaurants</Link>
          <Link to="/hebergements" onClick={() => setOpen(false)} className="py-2">Hébergements</Link>
          <Link to="/transport" onClick={() => setOpen(false)} className="py-2">Transport</Link>
        </div>
      )}
    </header>
    <PriceTicker />
  </>
  );
}
